import { Typography } from "antd";
import { Link } from "react-router-dom";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useDocumentCategoriesQuery } from "../../../../../../../api/graphql/queries";
import { useFetchDocumentsQuery } from "../../../../../../../api/graphql/queries/documents/documents.generated";
import { UserFibuFinalCheck } from "../../../../../../common/FibuFinalCheck";
import {
  KontaxUserContext,
  useKontaxUserContext,
} from "../../../../../../contexts/KontaxUserContext";
import {
  BANK_STATEMENTS_TAB_FIRST_YEAR,
  getBankStatementsLink,
} from "../../../TaxHome/tabParam";
import {
  useUpdateUserContext,
  useUserContext,
} from "../../../../contexts/UserContext";
import { formatDate } from "../../../../utils";
import { EMPTY_INDICATOR } from "../../../TradeTaxDeclaration/constants";
import { OverviewBlock, OverviewRow } from "./styledComponents";

const { Text, Title } = Typography;

// The customer's bank statement confirmation only exists from this tax year
// on (backend constant BANK_STATEMENTS_REQUIRED_YEAR in kontax-backend).
const BANK_STATEMENTS_REQUIRED_YEAR = 2025;
const BANK_STATEMENTS_CATEGORY_NAMES = ["BANK_STATEMENTS"];

const Row = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <OverviewRow>
    <Text type="secondary">{label}</Text>
    <Text>{value}</Text>
  </OverviewRow>
);

const useLatestBankStatementUpload = (taxYear: number) => {
  const user = useUserContext();
  const email = user?.email;

  const { data: categoriesData } = useDocumentCategoriesQuery({
    skip: !email,
    variables: { categoryNames: BANK_STATEMENTS_CATEGORY_NAMES },
  });
  const categoryIds = categoriesData?.documentCategories
    .map(({ id }) => id)
    .filter((id): id is string => !!id);

  const { data: documentsData } = useFetchDocumentsQuery({
    skip: !email || !categoryIds?.length,
    variables: { email: email!, year: taxYear, categoryIds },
  });

  if (!documentsData) {
    return undefined;
  }

  const latest = documentsData.documents
    .map(({ createdAt }) => createdAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  return latest ?? null;
};

const BookkeepingRows = ({ taxYear }: { taxYear: number }) => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "completenessOverview.bookkeeping",
  });
  const pageUser = useUserContext();
  // The Fibu control saves into the KontaxUserContext copy of the customer.
  // Read that copy so the confirmation row follows a save (e.g. a RESET).
  // Fall back to the page's customer until the copy is set.
  const { user: editedUser } = useKontaxUserContext();
  const user = editedUser ?? pageUser;
  const latestUpload = useLatestBankStatementUpload(taxYear);

  const fibuFinalCheck = user?.fibuFinalChecks?.find(
    ({ year }) => year === taxYear
  );

  const submittedAt = formatDate(fibuFinalCheck?.bankStatementsSubmittedAt);
  const complete =
    taxYear < BANK_STATEMENTS_REQUIRED_YEAR
      ? EMPTY_INDICATOR
      : submittedAt ?? t("open");

  // latestUpload is undefined while loading: show the empty indicator then.
  const uploaded =
    latestUpload === undefined
      ? EMPTY_INDICATOR
      : latestUpload
      ? t("uploadedYes", { date: formatDate(latestUpload) ?? EMPTY_INDICATOR })
      : t("uploadedNo");

  // Link to the customer's bank statements tab on Tax Home, where it exists.
  const bankStatementsLabel = (key: string): ReactNode =>
    user?.email && taxYear >= BANK_STATEMENTS_TAB_FIRST_YEAR ? (
      <Link to={getBankStatementsLink(user.email, taxYear)}>{t(key)}</Link>
    ) : (
      t(key)
    );

  return (
    <>
      {/* Same editable control as on Tax Home; it saves through the
          KontaxUserContext editUser and re-renders from the updated user. */}
      {user?.fibuFinalChecks && (
        <UserFibuFinalCheck
          year={taxYear}
          inlineLabel={String(t("fibuStatus"))}
        />
      )}
      <Row
        label={bankStatementsLabel("bankStatementsUploaded")}
        value={uploaded}
      />
      <Row
        label={bankStatementsLabel("bankStatementsComplete")}
        value={complete}
      />
    </>
  );
};

const BookkeepingSection = ({ taxYear }: { taxYear: number }) => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "completenessOverview.bookkeeping",
  });
  const user = useUserContext();
  const updatePageUser = useUpdateUserContext();

  return (
    <OverviewBlock>
      <Title level={4}>{t("title")}</Title>
      {/* Keyed by customer: a new customer gets a fresh copy, so the rows
          never show the previous customer's edited data, and a save that
          resolves after the switch cannot land on the new customer. A saved
          customer also goes back to the page, so the saved data survives a
          route change within the tax declaration area. */}
      <KontaxUserContext
        key={user?.email}
        user={user ?? undefined}
        onUserUpdated={updatePageUser}
      >
        <BookkeepingRows taxYear={taxYear} />
      </KontaxUserContext>
    </OverviewBlock>
  );
};

export default BookkeepingSection;
